import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'driver' | 'trainer' | 'admin' | 'manager';
  companyId: string;
  licenseNumber?: string;
  completedHours: number;
  requiredHours: number;
  avatar?: string;
  department?: string;
  permissions: string[];
  lastLogin?: Date;
  preferences: {
    theme: 'light' | 'dark';
    notifications: boolean;
    language: string;
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Mock user database
const mockUsers = {
  'driver@test.com': {
    id: '1',
    name: 'John Smith',
    email: 'driver@test.com',
    role: 'driver' as const,
    companyId: 'company1',
    licenseNumber: 'DL123456789',
    completedHours: 12,
    requiredHours: 35,
    department: 'Logistics',
    permissions: ['view_courses', 'take_training', 'view_progress'],
    preferences: {
      theme: 'light' as const,
      notifications: true,
      language: 'en'
    }
  },
  'admin@test.com': {
    id: '2',
    name: 'Sarah Johnson',
    email: 'admin@test.com',
    role: 'admin' as const,
    companyId: 'company1',
    completedHours: 45,
    requiredHours: 35,
    department: 'Administration',
    permissions: [
      'view_courses', 'create_courses', 'edit_courses', 'delete_courses',
      'view_users', 'create_users', 'edit_users', 'delete_users',
      'view_analytics', 'manage_system', 'manage_certificates'
    ],
    preferences: {
      theme: 'light' as const,
      notifications: true,
      language: 'en'
    }
  },
  'trainer@test.com': {
    id: '3',
    name: 'Mike Wilson',
    email: 'trainer@test.com',
    role: 'trainer' as const,
    companyId: 'company1',
    completedHours: 78,
    requiredHours: 35,
    department: 'Training',
    permissions: [
      'view_courses', 'create_courses', 'edit_courses',
      'view_users', 'view_analytics', 'manage_certificates'
    ],
    preferences: {
      theme: 'light' as const,
      notifications: true,
      language: 'en'
    }
  },
  'manager@test.com': {
    id: '4',
    name: 'Emma Davis',
    email: 'manager@test.com',
    role: 'manager' as const,
    companyId: 'company1',
    completedHours: 35,
    requiredHours: 35,
    department: 'Operations',
    permissions: [
      'view_courses', 'view_users', 'edit_users', 'view_analytics'
    ],
    preferences: {
      theme: 'light' as const,
      notifications: true,
      language: 'en'
    }
  }
};

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = () => {
      try {
        const storedUser = localStorage.getItem('dvsa_user');
        const sessionExpiry = localStorage.getItem('dvsa_session_expiry');
        
        if (storedUser && sessionExpiry) {
          const expiryDate = new Date(sessionExpiry);
          if (expiryDate > new Date()) {
            const userData = JSON.parse(storedUser);
            setUser(userData);
          } else {
            // Session expired, clear storage
            localStorage.removeItem('dvsa_user');
            localStorage.removeItem('dvsa_session_expiry');
          }
        }
      } catch (error) {
        console.error('Error checking existing session:', error);
        localStorage.removeItem('dvsa_user');
        localStorage.removeItem('dvsa_session_expiry');
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingSession();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check credentials
      const userData = mockUsers[email as keyof typeof mockUsers];
      
      if (userData && (password === 'demo123' || password === 'admin123')) {
        const userWithSession = {
          ...userData,
          lastLogin: new Date()
        };
        
        setUser(userWithSession);
        
        // Store session with 24 hour expiry
        const expiryDate = new Date();
        expiryDate.setHours(expiryDate.getHours() + 24);
        
        localStorage.setItem('dvsa_user', JSON.stringify(userWithSession));
        localStorage.setItem('dvsa_session_expiry', expiryDate.toISOString());
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('dvsa_user');
    localStorage.removeItem('dvsa_session_expiry');
    
    // Clear any other stored data
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('progress_') || key.startsWith('course_')) {
        localStorage.removeItem(key);
      }
    });
  };

  const updateUser = (updates: Partial<User>) => {
    if (!user) return;
    
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    
    // Update stored session
    localStorage.setItem('dvsa_user', JSON.stringify(updatedUser));
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    return user.permissions.includes(permission);
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUser,
    hasPermission
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}